import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Package, 
  Warehouse, 
  Truck, 
  HardHat, 
  Plus, 
  MagnifyingGlass,
  ArrowsLeftRight,
  ArrowDown,
  ArrowUp,
  Warning,
  Trash,
  PencilSimple,
  MapPin,
  CurrencyEur,
  Camera,
  FileText,
  Check,
  X,
  SpinnerGap,
  Receipt,
  FunnelSimple
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LOCATION_TYPES = {
  warehouse: { label: 'Entrepôt', icon: Warehouse, color: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' },
  vehicle: { label: 'Véhicule', icon: Truck, color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' },
  worksite: { label: 'Chantier', icon: HardHat, color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' }
};

const CATEGORIES = ['FOURNITURES', 'OUTILLAGE', 'CONSOMMABLES'];

function Stock() {
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showLowStock, setShowLowStock] = useState(false);
  
  // Dialogs
  const [locationDialog, setLocationDialog] = useState(false);
  const [itemDialog, setItemDialog] = useState(false);
  const [movementDialog, setMovementDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [invoiceDialog, setInvoiceDialog] = useState(false);
  const [invoiceReviewDialog, setInvoiceReviewDialog] = useState(false);
  
  // Invoice upload state
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [extractedInvoice, setExtractedInvoice] = useState(null);
  const [invoiceForm, setInvoiceForm] = useState({
    supplier_name: '',
    invoice_number: '',
    invoice_date: '',
    items: [],
    total_ht: 0,
    total_ttc: 0,
    tva_amount: 0,
    target_location_id: '',
    linked_quote_id: '',
    notes: ''
  });
  const [supplierInvoices, setSupplierInvoices] = useState([]);
  
  // Form data
  const [locationForm, setLocationForm] = useState({ name: '', location_type: 'warehouse', address: '' });
  const [itemForm, setItemForm] = useState({
    location_id: '',
    name: '',
    description: '',
    category: 'FOURNITURES',
    unit: 'unité',
    quantity: 0,
    unit_price: 0,
    min_quantity: 0,
    supplier: '',
    reference: ''
  });
  const [movementForm, setMovementForm] = useState({
    item_id: '',
    movement_type: 'in',
    quantity: 0,
    to_location_id: '',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, [selectedLocation, showLowStock]);

  async function loadData() {
    setLoading(true);
    try {
      const [locRes, itemRes, summaryRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/stock/locations`),
        axios.get(`${API}/stock/items`, {
          params: {
            location_id: selectedLocation || undefined,
            low_stock: showLowStock || undefined
          }
        }),
        axios.get(`${API}/stock/summary`),
        axios.get(`${API}/stock/supplier-invoices?limit=10`)
      ]);
      setLocations(locRes.data);
      setItems(itemRes.data);
      setSummary(summaryRes.data);
      setSupplierInvoices(invoicesRes.data);
    } catch (error) {
      toast.error('Erreur de chargement');
    }
    setLoading(false);
  }

  async function createLocation() {
    try {
      await axios.post(`${API}/stock/locations`, locationForm);
      toast.success('Emplacement créé');
      setLocationDialog(false);
      setLocationForm({ name: '', location_type: 'warehouse', address: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  }

  async function deleteLocation(locationId) {
    if (!window.confirm('Supprimer cet emplacement ?')) return;
    try {
      await axios.delete(`${API}/stock/locations/${locationId}`);
      toast.success('Emplacement supprimé');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  }

  async function saveItem() {
    try {
      if (editingItem) {
        await axios.put(`${API}/stock/items/${editingItem.item_id}`, itemForm);
        toast.success('Article mis à jour');
      } else {
        await axios.post(`${API}/stock/items`, itemForm);
        toast.success('Article créé');
      }
      setItemDialog(false);
      setEditingItem(null);
      setItemForm({
        location_id: locations[0]?.location_id || '',
        name: '',
        description: '',
        category: 'FOURNITURES',
        unit: 'unité',
        quantity: 0,
        unit_price: 0,
        min_quantity: 0,
        supplier: '',
        reference: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  }

  async function deleteItem(itemId) {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await axios.delete(`${API}/stock/items/${itemId}`);
      toast.success('Article supprimé');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  }

  async function recordMovement() {
    try {
      await axios.post(`${API}/stock/movements`, movementForm);
      toast.success('Mouvement enregistré');
      setMovementDialog(false);
      setMovementForm({ item_id: '', movement_type: 'in', quantity: 0, to_location_id: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  }

  function openEditItem(item) {
    setEditingItem(item);
    setItemForm({
      location_id: item.location_id,
      name: item.name,
      description: item.description || '',
      category: item.category,
      unit: item.unit,
      quantity: item.quantity,
      unit_price: item.unit_price,
      min_quantity: item.min_quantity,
      supplier: item.supplier || '',
      reference: item.reference || ''
    });
    setItemDialog(true);
  }

  function openMovement(item, type) {
    setMovementForm({
      item_id: item.item_id,
      movement_type: type,
      quantity: 0,
      to_location_id: '',
      notes: ''
    });
    setMovementDialog(true);
  }

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle invoice file upload
  async function handleInvoiceUpload(event) {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez JPEG, PNG ou PDF.');
      return;
    }

    // Check file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 10 Mo)');
      return;
    }

    setUploadingInvoice(true);
    setInvoiceDialog(false);

    try {
      // Convert to base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result.split(',')[1]; // Remove data:image/...;base64,
          resolve(result);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      // Send to API for analysis
      const response = await axios.post(`${API}/stock/supplier-invoices/analyze`, {
        file_base64: base64,
        file_type: file.type.startsWith('image/') ? 'image' : 'pdf'
      });

      const { invoice_id, extracted_data } = response.data;

      // Populate the form with extracted data
      setExtractedInvoice({
        invoice_id,
        file_preview: `data:${file.type};base64,${base64}`
      });

      setInvoiceForm({
        invoice_id,
        supplier_name: extracted_data.supplier_name || '',
        invoice_number: extracted_data.invoice_number || '',
        invoice_date: extracted_data.invoice_date || '',
        items: (extracted_data.items || []).map((item, idx) => ({
          ...item,
          id: idx,
          description: item.description || '',
          quantity: item.quantity || 0,
          unit: item.unit || 'unité',
          unit_price: item.unit_price || 0,
          total: item.total || 0,
          category: item.category || 'FOURNITURES'
        })),
        total_ht: extracted_data.total_ht || 0,
        total_ttc: extracted_data.total_ttc || 0,
        tva_amount: extracted_data.tva_amount || 0,
        target_location_id: locations[0]?.location_id || '',
        linked_quote_id: '',
        notes: ''
      });

      setInvoiceReviewDialog(true);
      toast.success('Facture analysée ! Vérifiez les données extraites.');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'analyse');
    } finally {
      setUploadingInvoice(false);
    }
  }

  // Confirm invoice and update stock
  async function confirmInvoice() {
    if (!invoiceForm.target_location_id) {
      toast.error('Sélectionnez un emplacement de destination');
      return;
    }

    if (invoiceForm.items.length === 0) {
      toast.error('Aucun article à enregistrer');
      return;
    }

    try {
      const payload = {
        invoice_id: extractedInvoice.invoice_id,
        supplier_name: invoiceForm.supplier_name,
        invoice_number: invoiceForm.invoice_number,
        invoice_date: invoiceForm.invoice_date,
        items: invoiceForm.items.map(item => ({
          description: item.description,
          quantity: parseFloat(item.quantity) || 0,
          unit: item.unit,
          unit_price: parseFloat(item.unit_price) || 0,
          total: parseFloat(item.total) || 0,
          category: item.category
        })),
        total_ht: parseFloat(invoiceForm.total_ht) || 0,
        total_ttc: parseFloat(invoiceForm.total_ttc) || 0,
        tva_amount: parseFloat(invoiceForm.tva_amount) || 0,
        target_location_id: invoiceForm.target_location_id,
        linked_quote_id: invoiceForm.linked_quote_id || null,
        notes: invoiceForm.notes || null
      };

      await axios.post(`${API}/stock/supplier-invoices/confirm`, payload);

      toast.success('Stock mis à jour avec succès !');
      setInvoiceReviewDialog(false);
      setExtractedInvoice(null);
      setInvoiceForm({
        supplier_name: '',
        invoice_number: '',
        invoice_date: '',
        items: [],
        total_ht: 0,
        total_ttc: 0,
        tva_amount: 0,
        target_location_id: '',
        linked_quote_id: '',
        notes: ''
      });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de confirmation');
    }
  }

  // Update invoice item
  function updateInvoiceItem(index, field, value) {
    const newItems = [...invoiceForm.items];
    newItems[index][field] = value;
    
    // Auto-calculate total if quantity or unit_price changes
    if (field === 'quantity' || field === 'unit_price') {
      const qty = parseFloat(newItems[index].quantity) || 0;
      const price = parseFloat(newItems[index].unit_price) || 0;
      newItems[index].total = Math.round(qty * price * 100) / 100;
    }
    
    setInvoiceForm({ ...invoiceForm, items: newItems });
  }

  // Add new item to invoice
  function addInvoiceItem() {
    setInvoiceForm({
      ...invoiceForm,
      items: [
        ...invoiceForm.items,
        {
          id: Date.now(),
          description: '',
          quantity: 1,
          unit: 'unité',
          unit_price: 0,
          total: 0,
          category: 'FOURNITURES'
        }
      ]
    });
  }

  // Remove item from invoice
  function removeInvoiceItem(index) {
    const newItems = invoiceForm.items.filter((_, i) => i !== index);
    setInvoiceForm({ ...invoiceForm, items: newItems });
  }

  if (loading && !summary) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-10 h-10 border-3 border-amber-500/30 border-t-amber-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="py-6 space-y-6" 
      data-testid="stock-page"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Package size={20} className="text-amber-600" weight="duotone" />
          </div>
          <span className="text-lg font-semibold text-foreground">Stock</span>
          <span className="text-sm text-muted-foreground">({items.length})</span>
        </div>
        
        <button 
          onClick={() => setInvoiceDialog(true)}
          className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 hover:bg-emerald-200 dark:hover:bg-emerald-900/50 flex items-center justify-center transition-colors"
          data-testid="upload-invoice-btn"
        >
          <Receipt size={20} className="text-emerald-600" weight="duotone" />
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 gap-3">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="premium-card p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Warehouse size={20} className="text-blue-600" weight="duotone" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Emplacements</p>
                <p className="text-xl font-bold text-foreground">{summary.location_count}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="premium-card p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                <Package size={20} className="text-emerald-600" weight="duotone" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Articles</p>
                <p className="text-xl font-bold text-foreground">{summary.total_items}</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="premium-card p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <CurrencyEur size={20} className="text-amber-600" weight="duotone" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Valeur</p>
                <p className="text-xl font-bold text-foreground">{summary.total_value?.toLocaleString('fr-FR')} €</p>
              </div>
            </div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className={`premium-card p-4 ${summary.low_stock_count > 0 ? 'ring-2 ring-red-200 dark:ring-red-900' : ''}`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                summary.low_stock_count > 0 
                  ? 'bg-red-100 dark:bg-red-900/30' 
                  : 'bg-slate-100 dark:bg-slate-800'
              }`}>
                <Warning size={20} className={summary.low_stock_count > 0 ? 'text-red-600' : 'text-muted-foreground'} weight="duotone" />
              </div>
              <div>
                <p className={`text-xs ${summary.low_stock_count > 0 ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'}`}>Stock bas</p>
                <p className={`text-xl font-bold ${summary.low_stock_count > 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>{summary.low_stock_count}</p>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Location Filter Pills */}
      <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
        <button
          onClick={() => setSelectedLocation(null)}
          className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
            selectedLocation === null
              ? 'bg-black dark:bg-white text-white dark:text-black'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          Tous
        </button>
        {locations.map(loc => {
          const TypeInfo = LOCATION_TYPES[loc.location_type] || LOCATION_TYPES.warehouse;
          const Icon = TypeInfo.icon;
          return (
            <button
              key={loc.location_id}
              onClick={() => setSelectedLocation(loc.location_id)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
                selectedLocation === loc.location_id
                  ? 'bg-black dark:bg-white text-white dark:text-black'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              <Icon size={16} weight={selectedLocation === loc.location_id ? 'fill' : 'regular'} />
              {loc.name}
              <span className="opacity-70">({loc.item_count})</span>
            </button>
          );
        })}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <button
          onClick={() => setShowLowStock(!showLowStock)}
          className={`flex-1 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all ${
            showLowStock 
              ? 'bg-red-500 text-white' 
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
        >
          <Warning size={18} weight={showLowStock ? 'fill' : 'regular'} />
          Stock bas
        </button>
        <button
          onClick={() => setLocationDialog(true)}
          className="flex-1 py-3 rounded-xl bg-muted hover:bg-muted/80 text-sm font-medium text-foreground flex items-center justify-center gap-2 transition-colors"
          data-testid="add-location-btn"
        >
          <MapPin size={18} />
          Emplacement
        </button>
        <button
          onClick={() => { setEditingItem(null); setItemForm({ ...itemForm, location_id: locations[0]?.location_id || '' }); setItemDialog(true); }}
          className="flex-1 py-3 rounded-xl bg-black dark:bg-white text-white dark:text-black text-sm font-medium flex items-center justify-center gap-2 transition-colors hover:opacity-90"
          data-testid="add-item-btn"
        >
          <Plus size={18} weight="bold" />
          Article
        </button>
      </div>

      {/* Items List */}
      <div className="space-y-3">
        {filteredItems.length === 0 ? (
          <div className="premium-card p-12 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
              <Package size={32} className="text-muted-foreground" weight="duotone" />
            </div>
            <p className="text-muted-foreground">
              {showLowStock ? 'Aucun article en stock bas' : 'Aucun article trouvé'}
            </p>
          </div>
        ) : (
          filteredItems.map((item, index) => {
            const isLowStock = item.quantity <= item.min_quantity && item.min_quantity > 0;
            return (
              <motion.div
                key={item.item_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className={`premium-card p-4 ${isLowStock ? 'ring-2 ring-red-200 dark:ring-red-900' : ''}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-foreground">{item.name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        item.category === 'FOURNITURES' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400' :
                        item.category === 'OUTILLAGE' ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400' :
                        'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300'
                      }`}>
                        {item.category}
                      </span>
                      {isLowStock && <Warning size={16} className="text-red-500" weight="fill" />}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{item.location_name}</p>
                    {item.reference && <p className="text-xs text-muted-foreground mt-1">Réf: {item.reference}</p>}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className={`text-lg font-bold ${isLowStock ? 'text-red-600 dark:text-red-400' : 'text-foreground'}`}>
                      {item.quantity} {item.unit}
                    </p>
                    <p className="text-xs text-muted-foreground">{item.unit_price?.toFixed(2)} €/u</p>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                  <button
                    onClick={() => openMovement(item, 'in')}
                    className="flex-1 flex items-center justify-center gap-1 py-2 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 hover:bg-emerald-200 dark:hover:bg-emerald-900/50 text-sm font-medium text-emerald-700 dark:text-emerald-400 transition-colors"
                  >
                    <ArrowDown size={16} weight="bold" />
                    Entrée
                  </button>
                  <button
                    onClick={() => openMovement(item, 'out')}
                    className="flex-1 flex items-center justify-center gap-1 py-2 rounded-xl bg-red-100 dark:bg-red-900/30 hover:bg-red-200 dark:hover:bg-red-900/50 text-sm font-medium text-red-700 dark:text-red-400 transition-colors"
                  >
                    <ArrowUp size={16} weight="bold" />
                    Sortie
                  </button>
                  <button
                    onClick={() => openMovement(item, 'transfer')}
                    className="flex-1 flex items-center justify-center gap-1 py-2 rounded-xl bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 text-sm font-medium text-blue-700 dark:text-blue-400 transition-colors"
                  >
                    <ArrowsLeftRight size={16} weight="bold" />
                    Transfert
                  </button>
                  <button
                    onClick={() => openEditItem(item)}
                    className="w-10 h-10 rounded-xl bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
                  >
                    <PencilSimple size={18} className="text-muted-foreground" />
                  </button>
                  <button
                    onClick={() => deleteItem(item.item_id)}
                    className="w-10 h-10 rounded-xl bg-muted hover:bg-red-100 dark:hover:bg-red-900/30 flex items-center justify-center transition-colors"
                  >
                    <Trash size={18} className="text-red-500" />
                  </button>
                </div>
              </motion.div>
            );
          })
        )}
      </div>

      {/* Location Dialog */}
      <Dialog open={locationDialog} onOpenChange={setLocationDialog}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Nouvel emplacement</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Nom</Label>
              <Input
                value={locationForm.name}
                onChange={(e) => setLocationForm({ ...locationForm, name: e.target.value })}
                placeholder="Ex: Entrepôt principal, Camion, Chantier Dupont..."
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Type</Label>
              <div className="flex gap-2 mt-2">
                {Object.entries(LOCATION_TYPES).map(([key, { label, icon: Icon }]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setLocationForm({ ...locationForm, location_type: key })}
                    className={`flex-1 py-3 rounded-xl text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                      locationForm.location_type === key
                        ? 'bg-black dark:bg-white text-white dark:text-black'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                  >
                    <Icon size={18} weight={locationForm.location_type === key ? 'fill' : 'regular'} />
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Adresse (optionnel)</Label>
              <Input
                value={locationForm.address}
                onChange={(e) => setLocationForm({ ...locationForm, address: e.target.value })}
                placeholder="Adresse de l'emplacement"
                className="mt-1 rounded-xl"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setLocationDialog(false)} className="flex-1 py-3 rounded-xl bg-muted text-foreground font-medium hover:bg-muted/80 transition-colors">
              Annuler
            </button>
            <button onClick={createLocation} className="flex-1 py-3 rounded-xl bg-black dark:bg-white text-white dark:text-black font-medium hover:opacity-90 transition-colors">
              Créer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Item Dialog */}
      <Dialog open={itemDialog} onOpenChange={setItemDialog}>
        <DialogContent className="max-w-lg rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">{editingItem ? 'Modifier l\'article' : 'Nouvel article'}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Label className="text-sm font-medium text-muted-foreground">Nom *</Label>
              <Input
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                placeholder="Nom de l'article"
                className="mt-1 rounded-xl"
              />
            </div>
            <div className="col-span-2">
              <Label className="text-sm font-medium text-muted-foreground">Description</Label>
              <Input
                value={itemForm.description}
                onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                placeholder="Description optionnelle"
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Emplacement *</Label>
              <select
                className="w-full mt-1 p-3 border border-border rounded-xl bg-background text-foreground"
                value={itemForm.location_id}
                onChange={(e) => setItemForm({ ...itemForm, location_id: e.target.value })}
              >
                <option value="">Sélectionner...</option>
                {locations.map(loc => (
                  <option key={loc.location_id} value={loc.location_id}>{loc.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Catégorie</Label>
              <select
                className="w-full mt-1 p-3 border border-border rounded-xl bg-background text-foreground"
                value={itemForm.category}
                onChange={(e) => setItemForm({ ...itemForm, category: e.target.value })}
              >
                {CATEGORIES.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Quantité</Label>
              <Input
                type="number"
                value={itemForm.quantity}
                onChange={(e) => setItemForm({ ...itemForm, quantity: parseFloat(e.target.value) || 0 })}
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Unité</Label>
              <select
                className="w-full mt-1 p-3 border border-border rounded-xl bg-background text-foreground"
                value={itemForm.unit}
                onChange={(e) => setItemForm({ ...itemForm, unit: e.target.value })}
              >
                <option value="unité">Unité</option>
                <option value="m²">m²</option>
                <option value="ml">ml</option>
                <option value="kg">kg</option>
                <option value="L">L</option>
                <option value="lot">Lot</option>
                <option value="sac">Sac</option>
                <option value="rouleau">Rouleau</option>
              </select>
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Prix unitaire (€)</Label>
              <Input
                type="number"
                step="0.01"
                value={itemForm.unit_price}
                onChange={(e) => setItemForm({ ...itemForm, unit_price: parseFloat(e.target.value) || 0 })}
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Seuil alerte</Label>
              <Input
                type="number"
                value={itemForm.min_quantity}
                onChange={(e) => setItemForm({ ...itemForm, min_quantity: parseFloat(e.target.value) || 0 })}
                placeholder="Stock minimum"
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Fournisseur</Label>
              <Input
                value={itemForm.supplier}
                onChange={(e) => setItemForm({ ...itemForm, supplier: e.target.value })}
                className="mt-1 rounded-xl"
              />
            </div>
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Référence</Label>
              <Input
                value={itemForm.reference}
                onChange={(e) => setItemForm({ ...itemForm, reference: e.target.value })}
                className="mt-1 rounded-xl"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => { setItemDialog(false); setEditingItem(null); }} className="flex-1 py-3 rounded-xl bg-muted text-foreground font-medium hover:bg-muted/80 transition-colors">
              Annuler
            </button>
            <button onClick={saveItem} className="flex-1 py-3 rounded-xl bg-black dark:bg-white text-white dark:text-black font-medium hover:opacity-90 transition-colors">
              {editingItem ? 'Modifier' : 'Créer'}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement Dialog */}
      <Dialog open={movementDialog} onOpenChange={setMovementDialog}>
        <DialogContent className="rounded-3xl">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">
              {movementForm.movement_type === 'in' && 'Entrée en stock'}
              {movementForm.movement_type === 'out' && 'Sortie de stock'}
              {movementForm.movement_type === 'transfer' && 'Transfert'}
              {movementForm.movement_type === 'return' && 'Retour chantier'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Quantité *</Label>
              <Input
                type="number"
                value={movementForm.quantity}
                onChange={(e) => setMovementForm({ ...movementForm, quantity: parseFloat(e.target.value) || 0 })}
                className="mt-1 rounded-xl"
              />
            </div>
            {movementForm.movement_type === 'transfer' && (
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Destination *</Label>
                <select
                  className="w-full mt-1 p-3 border border-border rounded-xl bg-background text-foreground"
                  value={movementForm.to_location_id}
                  onChange={(e) => setMovementForm({ ...movementForm, to_location_id: e.target.value })}
                >
                  <option value="">Sélectionner...</option>
                  {locations.map(loc => (
                    <option key={loc.location_id} value={loc.location_id}>{loc.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <Label className="text-sm font-medium text-muted-foreground">Notes</Label>
              <Input
                value={movementForm.notes}
                onChange={(e) => setMovementForm({ ...movementForm, notes: e.target.value })}
                placeholder="Notes optionnelles"
                className="mt-1 rounded-xl"
              />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <button onClick={() => setMovementDialog(false)} className="flex-1 py-3 rounded-xl bg-muted text-foreground font-medium hover:bg-muted/80 transition-colors">
              Annuler
            </button>
            <button onClick={recordMovement} className="flex-1 py-3 rounded-xl bg-black dark:bg-white text-white dark:text-black font-medium hover:opacity-90 transition-colors">
              Enregistrer
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Upload Dialog */}
      <Dialog open={invoiceDialog} onOpenChange={setInvoiceDialog}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
              <Receipt size={20} className="text-emerald-600" weight="duotone" />
              Importer une facture fournisseur
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Photographiez ou importez une facture/bon de livraison de matériaux. 
              L'IA analysera le document et vous pourrez vérifier/corriger les données avant la mise à jour du stock.
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handleInvoiceUpload}
                  data-testid="invoice-photo-input"
                />
                <div className="border-2 border-dashed border-border rounded-2xl p-6 text-center hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors">
                  <Camera size={32} className="mx-auto mb-2 text-muted-foreground" weight="duotone" />
                  <p className="text-sm font-medium text-foreground">Photo</p>
                  <p className="text-xs text-muted-foreground">JPEG, PNG</p>
                </div>
              </label>
              
              <label className="cursor-pointer">
                <input
                  type="file"
                  accept="application/pdf,image/jpeg,image/png"
                  className="hidden"
                  onChange={handleInvoiceUpload}
                  data-testid="invoice-file-input"
                />
                <div className="border-2 border-dashed border-border rounded-2xl p-6 text-center hover:border-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors">
                  <FileText size={32} className="mx-auto mb-2 text-muted-foreground" weight="duotone" />
                  <p className="text-sm font-medium text-foreground">Document</p>
                  <p className="text-xs text-muted-foreground">PDF, Image</p>
                </div>
              </label>
            </div>
            
            {supplierInvoices.length > 0 && (
              <div className="border-t border-border pt-4 mt-4">
                <p className="text-xs text-muted-foreground mb-2">Dernières factures importées</p>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {supplierInvoices.slice(0, 3).map(inv => (
                    <div key={inv.invoice_id} className="flex items-center justify-between text-sm p-3 bg-muted/50 rounded-xl">
                      <span className="font-medium text-foreground truncate">{inv.supplier_name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        inv.status === 'processed' ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                      }`}>
                        {inv.status === 'processed' ? 'Traité' : 'En attente'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <button onClick={() => setInvoiceDialog(false)} className="w-full py-3 rounded-xl bg-muted text-foreground font-medium hover:bg-muted/80 transition-colors">
              Annuler
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Review/Validation Dialog */}
      <Dialog open={invoiceReviewDialog} onOpenChange={setInvoiceReviewDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
              <Check size={20} className="text-emerald-600" weight="duotone" />
              Vérification des données extraites
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Image preview */}
            {extractedInvoice?.file_preview && (
              <div className="border border-border rounded-2xl overflow-hidden bg-muted/50">
                <img 
                  src={extractedInvoice.file_preview} 
                  alt="Facture" 
                  className="w-full h-auto max-h-[400px] object-contain"
                />
              </div>
            )}
            
            {/* Right: Extracted data form */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-sm font-medium text-muted-foreground">Fournisseur *</Label>
                  <Input
                    value={invoiceForm.supplier_name}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, supplier_name: e.target.value })}
                    placeholder="Nom du fournisseur"
                    className="mt-1 rounded-xl"
                    data-testid="invoice-supplier-name"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">N° Facture</Label>
                  <Input
                    value={invoiceForm.invoice_number}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_number: e.target.value })}
                    placeholder="FAC-XXX"
                    className="mt-1 rounded-xl"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Date</Label>
                  <Input
                    value={invoiceForm.invoice_date}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, invoice_date: e.target.value })}
                    placeholder="JJ/MM/AAAA"
                    className="mt-1 rounded-xl"
                  />
                </div>
              </div>
              
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Emplacement de destination *</Label>
                <select
                  className="w-full mt-1 p-3 border border-border rounded-xl bg-background text-foreground"
                  value={invoiceForm.target_location_id}
                  onChange={(e) => setInvoiceForm({ ...invoiceForm, target_location_id: e.target.value })}
                  data-testid="invoice-target-location"
                >
                  <option value="">Sélectionner l'emplacement...</option>
                  {locations.map(loc => (
                    <option key={loc.location_id} value={loc.location_id}>{loc.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="grid grid-cols-3 gap-2 p-4 bg-muted/50 rounded-2xl">
                <div>
                  <Label className="text-xs text-muted-foreground">Total HT</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={invoiceForm.total_ht}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, total_ht: parseFloat(e.target.value) || 0 })}
                    className="text-sm rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">TVA</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={invoiceForm.tva_amount}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, tva_amount: parseFloat(e.target.value) || 0 })}
                    className="text-sm rounded-xl mt-1"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Total TTC</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={invoiceForm.total_ttc}
                    onChange={(e) => setInvoiceForm({ ...invoiceForm, total_ttc: parseFloat(e.target.value) || 0 })}
                    className="text-sm font-medium rounded-xl mt-1"
                  />
                </div>
              </div>
            </div>
          </div>
          
          {/* Items table */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-base font-semibold">Articles extraits</Label>
              <button onClick={addInvoiceItem} className="px-3 py-1.5 rounded-xl bg-muted hover:bg-muted/80 text-sm font-medium text-foreground flex items-center gap-1 transition-colors">
                <Plus size={14} weight="bold" /> Ajouter
              </button>
            </div>
            
            <div className="border border-border rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-3 font-medium text-muted-foreground">Description</th>
                    <th className="text-right p-3 font-medium text-muted-foreground w-20">Qté</th>
                    <th className="text-left p-3 font-medium text-muted-foreground w-24">Unité</th>
                    <th className="text-right p-3 font-medium text-muted-foreground w-24">Prix unit.</th>
                    <th className="text-right p-3 font-medium text-muted-foreground w-24">Total</th>
                    <th className="text-center p-3 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceForm.items.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-muted-foreground">
                        Aucun article extrait. Ajoutez-en manuellement.
                      </td>
                    </tr>
                  ) : (
                    invoiceForm.items.map((item, idx) => (
                      <tr key={item.id || idx} className="border-t border-border">
                        <td className="p-2">
                          <Input
                            value={item.description}
                            onChange={(e) => updateInvoiceItem(idx, 'description', e.target.value)}
                            className="text-sm h-9 rounded-lg"
                            placeholder="Nom du produit"
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            value={item.quantity}
                            onChange={(e) => updateInvoiceItem(idx, 'quantity', e.target.value)}
                            className="text-sm h-9 text-right rounded-lg"
                          />
                        </td>
                        <td className="p-2">
                          <select
                            className="w-full p-2 border border-border rounded-lg text-sm h-9 bg-background text-foreground"
                            value={item.unit}
                            onChange={(e) => updateInvoiceItem(idx, 'unit', e.target.value)}
                          >
                            <option value="unité">unité</option>
                            <option value="m²">m²</option>
                            <option value="ml">ml</option>
                            <option value="kg">kg</option>
                            <option value="L">L</option>
                            <option value="sac">sac</option>
                            <option value="lot">lot</option>
                            <option value="rouleau">rouleau</option>
                          </select>
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={item.unit_price}
                            onChange={(e) => updateInvoiceItem(idx, 'unit_price', e.target.value)}
                            className="text-sm h-9 text-right rounded-lg"
                          />
                        </td>
                        <td className="p-2 text-right font-medium text-foreground">
                          {(parseFloat(item.total) || 0).toFixed(2)} €
                        </td>
                        <td className="p-2 text-center">
                          <button
                            onClick={() => removeInvoiceItem(idx)}
                            className="w-8 h-8 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 flex items-center justify-center transition-colors"
                          >
                            <X size={14} className="text-red-500" weight="bold" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
          <DialogFooter className="mt-4 gap-2">
            <button onClick={() => {
              setInvoiceReviewDialog(false);
              setExtractedInvoice(null);
            }} className="flex-1 py-3 rounded-xl bg-muted text-foreground font-medium hover:bg-muted/80 transition-colors">
              Annuler
            </button>
            <button onClick={confirmInvoice} className="flex-1 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-medium flex items-center justify-center gap-2 transition-colors" data-testid="confirm-invoice-btn">
              <Check size={18} weight="bold" />
              Valider et mettre à jour le stock
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Loading overlay for invoice processing */}
      {uploadingInvoice && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="premium-card p-8 text-center">
            <SpinnerGap size={48} className="animate-spin mx-auto mb-4 text-emerald-600" />
            <p className="font-medium text-foreground">Analyse de la facture en cours...</p>
            <p className="text-sm text-muted-foreground mt-1">L'IA extrait les informations</p>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default Stock;
